import { createUserTheme, deleteUserTheme, setDefaultTheme, setSelectedTheme, updateUserTheme } from '@services/themes';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { GET_THEMES_KEY } from './useFetchThemes';
import { type Theme } from '../store/ThemeStore';
import { notify } from '@components/Presentation/helpers';

export default function useThemesAPI() {
  const [loading, setLoading] = useState(false);

  const { currentTeam, ownTeam } = useTeamsData((s) => ({ currentTeam: s.currentTeam, ownTeam: s.ownTeam }));

  const userDetailsId = currentTeam?.billing_account?.owner?.user_details_id;
  const ownerId = ownTeam?.billing_account?.owner?.user_details_id;

  const queryClient = useQueryClient();

  const createThemeApi = async (theme: Theme) => {
    setLoading(true);
    const res = await createUserTheme({ theme, userDetailsId, createdBy: ownerId });
    queryClient.invalidateQueries({
      queryKey: [GET_THEMES_KEY]
    });
    setLoading(false);
    if (res?.status) {
      notify('Theme duplicated successfully');
    } else {
      notify('Failed to duplicate theme', 'error');
    }
    return res;
  };

  const updateThemeApi = async (theme: Theme) => {
    setLoading(true);
    const res = await updateUserTheme({ theme, updatedBy: ownerId });
    queryClient.invalidateQueries({
      queryKey: [GET_THEMES_KEY]
    });
    setLoading(false);
    return res;
  };

  const deleteThemeApi = async (themeId: string, isDefaultTheme: boolean = false, darkThemeId?: string) => {
    setLoading(true);
    const res = await deleteUserTheme(themeId);
    if (isDefaultTheme && darkThemeId) {
      await setDefaultTheme({ themeId: darkThemeId, userDetailsId });
    }
    queryClient.invalidateQueries({
      queryKey: [GET_THEMES_KEY]
    });
    setLoading(false);
    if (res?.status) {
      notify('Theme deleted successfully');
    } else {
      notify('Failed to delete theme', 'error');
    }
    return res;
  };

  const applyDefaultThemeApi = async (themeId: string) => {
    setLoading(true);
    const res = await setDefaultTheme({ themeId, userDetailsId });
    queryClient.invalidateQueries({
      queryKey: [GET_THEMES_KEY]
    });
    setLoading(false);
    if (res?.status) {
      notify('Default theme updated');
    } else {
      notify('Failed to set default theme', 'error');
    }
    return res;
  };

  const applySelectedThemeApi = async (themeId: string, templateId: string) => {
    setLoading(true);
    const res = await setSelectedTheme({ themeId, templateId });
    setLoading(false);
    return res;
  };

  return {
    createThemeApi,
    updateThemeApi,
    applyDefaultThemeApi,
    applySelectedThemeApi,
    deleteThemeApi,
    loading
  };
}
